/* eslint-disable react/jsx-no-bind -- Controller's render prop + per-field handlers cannot be
   hoisted to useCallback (they close over the render-prop's `field`); same precedent as the
   other RHF wrappers. */
import noop from 'lodash/noop'
import React from 'react'
import { Controller } from 'react-hook-form'

import HHTimeField from './HHTimeField'

import type { SxProps, Theme } from '@mui/material/styles'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'

export type HHFormTimeFieldProps<TField extends FieldValues = FieldValues> = {
  name: FieldPath<TField>
  control: Control<TField>
  label?: string
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  sx?: SxProps<Theme>
  rules?: RegisterOptions<TField>
  onChange?: (value: string) => void
  // Forwarded to HHTimeField; TextField-level props go under `textField`.
  slotProps?: Record<string, unknown>
}

/**
 * react-hook-form time-of-day field: wraps HHTimeField in a Controller. The form
 * value stays the clock text the field emits, so a half-typed time never lands in
 * form state.
 */
const HHFormTimeField = <TField extends FieldValues = FieldValues>({
  name,
  control,
  rules = undefined,
  onChange = noop,
  ...rest
}: HHFormTimeFieldProps<TField>) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => {
      const { ref, onChange: onChangeHookForm, value, ...fieldRest } = field
      return (
        <HHTimeField
          inputRef={ref}
          value={value}
          onChange={(next: string) => {
            onChangeHookForm(next)
            onChange(next)
          }}
          {...fieldRest}
          {...rest}
        />
      )
    }}
    rules={rules}
  />
)

export default HHFormTimeField
